import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import portalClient from './portalClient';
import PortalLayout from './PortalLayout';
import { Button, Card, Input, Modal, Select, Skeleton, useToast } from '../design-system';
import './Portal.css';

const fmtDate = (d) =>
  (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const MODES = [
  { value: 'bank_transfer', label: 'Bank transfer / NEFT / RTGS' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'card', label: 'Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

// One invoice: the document itself, and the client's way of telling the firm
// they have paid it.
//
// The document is rendered SERVER-SIDE and dropped in as HTML, so the client
// sees byte-for-byte what the firm sees and what the print output contains.
// Rebuilding the layout in React would create a second version of a statutory
// document that could quietly diverge from the one the firm believes it issued.
function PortalInvoiceDetail() {
  const { id } = useParams();
  const toast = useToast();
  const [invoice, setInvoice] = useState(null);
  const [docHtml, setDocHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const frameRef = useRef(null);

  const [form, setForm] = useState({
    amount: '', tdsAmount: '', paidOn: todayISO(), mode: 'bank_transfer', reference: '', note: '',
  });
  const [proof, setProof] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await portalClient.get(`/api/portal/invoices/${id}`);
      setInvoice(res.data);
      setForm((f) => ({
        ...f,
        // ⚠️ PREFILLED WITH THE FULL BALANCE, AND TDS LEFT BLANK.
        //
        // This used to prefill `expectedReceipt` and the computed TDS, which
        // told the client "you deducted tax" before they said so. Whether they
        // are required to deduct depends on their own position — the annual
        // threshold under 194J, whether they are liable to deduct at all, any
        // lower-deduction certificate — none of which this firm knows. A client
        // who does not deduct had to notice the prefill was wrong and correct
        // two fields, and the ones who did not notice under-declared.
        //
        // So the deduction is entered where it is known: by whoever is paying,
        // at the time they pay.
        amount: String(res.data.amountDue > 0 ? res.data.amountDue : ''),
        tdsAmount: '',
      }));
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not load this invoice');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // The document HTML needs the auth header, so it is fetched rather than set
  // as an iframe src — a bare <iframe src> would carry no token and 401.
  useEffect(() => {
    portalClient
      .get(`/api/portal/invoices/${id}/document`, { responseType: 'text' })
      .then((res) => setDocHtml(res.data))
      .catch(() => setDocHtml(''));
  }, [id]);

  // srcDoc rather than dangerouslySetInnerHTML: the document ships its own
  // <style>, and injecting that into the portal would restyle the whole app.
  // The iframe also keeps a printed copy to just the document.
  useEffect(() => {
    const el = frameRef.current;
    if (!el || !docHtml) return;
    const fit = () => {
      try {
        el.style.height = `${el.contentWindow.document.body.scrollHeight + 32}px`;
      } catch { /* cross-origin can't happen with srcDoc, but never break render */ }
    };
    el.addEventListener('load', fit);
    return () => el.removeEventListener('load', fit);
  }, [docHtml]);

  const printDocument = () => {
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    w.focus();
    w.print();
  };

  const openReceipt = async (paymentId) => {
    // Same auth problem as the document — fetch it, then hand the browser a
    // blob so the receipt opens in its own tab.
    try {
      const res = await portalClient.get(`/api/portal/payments/${paymentId}/document`, { responseType: 'text' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/html' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.danger('Could not open that receipt');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount || 0);
    const tds = Number(form.tdsAmount || 0);
    if (amount + tds <= 0) { toast.danger('Enter the amount you paid'); return; }

    setSaving(true);
    try {
      // multipart because the proof file rides along — one request, so a
      // failed follow-up can't leave an orphaned upload.
      const body = new FormData();
      body.append('amount', String(amount));
      body.append('tdsAmount', String(tds));
      body.append('paidOn', form.paidOn);
      body.append('mode', form.mode);
      body.append('reference', form.reference);
      body.append('note', form.note);
      if (proof) body.append('proof', proof);

      const res = await portalClient.post(`/api/portal/invoices/${id}/payment-advice`, body);
      toast.success(res.data.message || 'Thank you — we will confirm shortly');
      setPayOpen(false);
      setProof(null);
      await load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not submit that');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PortalLayout title="Invoice"><Card><Skeleton.Text lines={6} /></Card></PortalLayout>;
  }
  if (!invoice) {
    return (
      <PortalLayout title="Invoice">
        <Card padding="lg">
          <div className="portal-empty">
            <span className="portal-empty-icon">🔍</span>
            <h3 className="portal-empty-title">Not found</h3>
            <p className="portal-muted">This invoice isn't available. <Link to="/portal/billing">Back to billing</Link></p>
          </div>
        </Card>
      </PortalLayout>
    );
  }

  const pending = (invoice.advices || []).find((a) => a.status === 'pending');
  const rejected = (invoice.advices || []).filter((a) => a.status === 'rejected');
  const canDeclare = !pending && !['paid', 'cancelled'].includes(invoice.status);

  return (
    <PortalLayout
      title={invoice.number || 'Invoice'}
      subtitle={invoice.isProforma ? 'Proforma invoice' : 'Tax invoice'}
    >
      <div className="portal-stack">
        <Card padding="md">
          <div className="portal-bill-summary">
            <div>
              <div className="portal-label">{invoice.amountDue > 0 ? 'Amount payable' : 'Total'}</div>
              {/* ⚠️ THE FULL BALANCE, never total-less-TDS. The headline figure
                  is what is owed; whether the client deducts is their call and
                  is captured when they declare the payment. Showing the net
                  here asserted a deduction that may not apply and left the firm
                  chasing a balance its own portal said was not due. */}
              <div className="portal-bill-total">
                {inr(invoice.amountDue > 0 ? invoice.amountDue : invoice.total)}
              </div>
              {/* Stated, not applied — the client decides and tells us below. */}
              {invoice.amountPaid === 0 && invoice.tdsAmount > 0 && (
                <div className="portal-muted">
                  If you are required to deduct TDS, that is {inr(invoice.tdsAmount)} @ {invoice.tdsPercent}%
                  u/s 194J on the fee — remit {inr(invoice.expectedReceipt)} and deposit the rest to the
                  Government on our behalf. Tell us below what you deducted.
                </div>
              )}
              {invoice.dueDate && invoice.amountDue > 0 && (
                <div className={invoice.isOverdue ? 'portal-overdue' : 'portal-muted'}>
                  {invoice.isOverdue ? 'Was due ' : 'Due '}{fmtDate(invoice.dueDate)}
                </div>
              )}
            </div>
            <div className="portal-bill-actions">
              <Button variant="secondary" onClick={printDocument} disabled={!docHtml}>
                Print / Save PDF
              </Button>
              {canDeclare && (
                <Button variant="primary" onClick={() => setPayOpen(true)}>I've paid this</Button>
              )}
            </div>
          </div>

          {invoice.isProforma && (
            <p className="portal-note-warn">
              This is a proforma invoice — not a tax invoice, and not valid for input tax credit.
              We'll issue the tax invoice once your payment is confirmed.
            </p>
          )}

          {pending && (
            <p className="portal-note-info">
              <strong>Awaiting our confirmation.</strong> You reported {inr(pending.amount + pending.tdsAmount)} paid
              on {fmtDate(pending.paidOn)}{pending.reference ? ` (ref ${pending.reference})` : ''}.
              We'll send a receipt once we've matched it against our bank.
            </p>
          )}

          {rejected.map((a) => (
            <p key={a.id} className="portal-note-warn">
              <strong>We couldn't confirm the payment you reported on {fmtDate(a.paidOn)}.</strong>
              {a.rejectionReason ? ` ${a.rejectionReason}` : ''} Please check and let us know again.
            </p>
          ))}
        </Card>

        {!!(invoice.receipts || []).length && (
          <Card padding="md">
            <div className="portal-card-head"><span className="portal-card-title">Receipts</span></div>
            <ul className="portal-list">
              {invoice.receipts.map((r) => (
                <li key={r.id} className="portal-list-row">
                  <span className="portal-list-main">
                    {r.receiptNumber || 'Receipt'}
                    <span className="portal-muted"> · {fmtDate(r.receivedOn)}</span>
                  </span>
                  <span className="portal-bill-right">
                    <span className="portal-bill-amount">{inr(r.settled)}</span>
                    <button type="button" className="portal-linkbtn" onClick={() => openReceipt(r.id)}>
                      View receipt
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card padding="none">
          {docHtml
            ? <iframe ref={frameRef} title="Invoice document" srcDoc={docHtml} className="portal-doc-frame" />
            : <div style={{ padding: 16 }}><Skeleton.Text lines={8} /></div>}
        </Card>
      </div>

      <Modal
        isOpen={payOpen}
        onClose={() => setPayOpen(false)}
        title="Tell us about your payment"
        size="md"
      >
        <form onSubmit={submit} className="portal-form">
          <p className="portal-muted" style={{ marginTop: 0 }}>
            This lets us know to look for it. Your invoice stays open until we've matched
            the payment against our bank — we'll email you a receipt as soon as we have.
          </p>

          <div className="portal-form-row">
            <Input
              label="Amount transferred"
              type="number" step="0.01" min="0" required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              helperText="What left your bank"
            />
            <Input
              label="TDS deducted"
              type="number" step="0.01" min="0"
              value={form.tdsAmount}
              onChange={(e) => setForm({ ...form, tdsAmount: e.target.value })}
              helperText="Leave blank if you didn&apos;t deduct any"
            />
          </div>

          <div className="portal-form-row">
            <Input
              label="Paid on" type="date" required
              value={form.paidOn}
              onChange={(e) => setForm({ ...form, paidOn: e.target.value })}
            />
            <Select
              label="How"
              value={form.mode}
              onChange={(e) => setForm({ ...form, mode: e.target.value })}
            >
              {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Select>
          </div>

          <Input
            label="Reference"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            placeholder="UTR / cheque no. / UPI ref"
            helperText="This is what we search the bank statement for — it helps a lot"
          />

          <Input
            label="Anything else"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Optional"
          />

          <div className="portal-field">
            <span className="portal-label">Proof of payment (optional)</span>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setProof(e.target.files?.[0] || null)}
            />
            {proof && <span className="portal-muted">{proof.name}</span>}
          </div>

          <div className="portal-form-actions">
            <Button type="button" variant="ghost" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" loading={saving}>Send to the firm</Button>
          </div>
        </form>
      </Modal>
    </PortalLayout>
  );
}

export default PortalInvoiceDetail;
